<template>
    <div class="flex flex-col gap-1">
        <div class="flex gap-2">
            <RedesignBaseInput
                v-model="inputValue"
                :placeholder="placeholder"
                :disabled="disabled"
                class="flex-1"
                @keyup.enter.native="add"
            />
            <RedesignBaseButton
                type="outline"
                size="small"
                :disabled="disabled || !inputValue.trim()"
                @click="add"
            >
                {{ addBtnLabel }}
            </RedesignBaseButton>
        </div>
        <div
            v-if="normalizedTags.length > 0 && showList"
            class="flex flex-wrap gap-1"
        >
            <div
                v-for="tag in normalizedTags"
                :key="tag"
                class="inline-flex items-center gap-1 rounded-full bg-gray-100 pl-2 pr-1 py-0.5 text-sm max-w-full"
            >
                <span class="truncate max-w-[200px]">{{ tag }}</span>
                <RedesignBaseIconButton
                    class="rounded-full text-gray hover:bg-white"
                    icon="X"
                    :size="14"
                    @click="remove(tag)"
                />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import RedesignBaseInput from '@/components/RedesignBaseComponents/Inputs/RedesignBaseInput.vue'
import RedesignBaseButton from '@/components/RedesignBaseComponents/Buttons/RedesignBaseButton.vue'
import RedesignBaseIconButton from '@/components/RedesignBaseComponents/Buttons/RedesignBaseIconButton.vue'
import { TranslateResult } from 'vue-i18n'
import i18n from '@/i18n'

export type TagMultiInputItem = string | { value: string }

/* Props */
const props = withDefaults(
    defineProps<{
        tags: TagMultiInputItem[]
        placeholder?: string | TranslateResult
        addLabel?: string | TranslateResult
        disabled?: boolean
        showList?: boolean
    }>(),
    {
        placeholder: '',
        addLabel: '',
        disabled: false,
        showList: true
    }
)

/* Emits */
const emit = defineEmits<{
    (e: 'add', value: string): void
    (e: 'remove', value: string): void
}>()

/* Data */
const inputValue = ref('')

/* Computed */
const normalizedTags = computed(() =>
  props.tags.map((item) => (typeof item === 'string' ? item : item.value))
)

const addBtnLabel = computed(() =>
    String(props.addLabel ?? '').trim() || String(i18n.t('common.add'))
)

function add () {
    if (props.disabled) return
    const trimmed = inputValue.value.trim()
    if (!trimmed || normalizedTags.value.includes(trimmed)) {
        inputValue.value = ''
        return
    }
    emit('add', trimmed)
    inputValue.value = ''
}

function remove (value: string) {
    if (props.disabled) return
    emit('remove', value)
}
</script>